import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { postTourOnboardingMutationFn } from '@/queries/users';
import type { TourOnboardingRequest } from '@/queries/users/types';
import { useSelector } from '@/redux/helper';
import { updateUserProperty } from '@/redux/slices/user/actions';

const useTourTracking = () => {
  const dispatch = useDispatch();
  const educatorOnboarding = useSelector((state) => state.user.educatorOnboarding);

  const { mutate: updateTourOnboarding, isPending } = useMutation({
    mutationFn: postTourOnboardingMutationFn,
    onSuccess: (response) => {
      if (response.user?.educatorOnboarding) {
        dispatch(updateUserProperty({ educatorOnboarding: response.user.educatorOnboarding }));
      }
    },
    onError: (error) => {
      console.error('Failed to update tour onboarding status:', error);
    },
  });

  const trackStarted = useCallback(() => {
    if (educatorOnboarding?.isOnboardingStarted || isPending) {
      return;
    }

    const payload: TourOnboardingRequest = {
      isOnboardingStarted: true,
      isOnboardingSkipped: false,
      isOnboardingCompleted: false,
    };

    updateTourOnboarding(payload);
  }, [educatorOnboarding?.isOnboardingStarted, isPending, updateTourOnboarding]);

  const trackSkip = useCallback(() => {
    if (isPending) return;

    const payload: TourOnboardingRequest = {
      isOnboardingSkipped: true,
      isOnboardingCompleted: false,
    };

    updateTourOnboarding(payload);
  }, [isPending, updateTourOnboarding]);

  const trackCompletion = useCallback(() => {
    if (isPending) return;

    const payload: TourOnboardingRequest = {
      isOnboardingSkipped: false,
      isOnboardingCompleted: true,
    };

    updateTourOnboarding(payload);
  }, [isPending, updateTourOnboarding]);

  return {
    trackStarted,
    trackSkip,
    trackCompletion,
    isUpdating: isPending,
  };
};

export default useTourTracking;
